import { useLanguage, useMessageActionContext } from '@/app/providers';
import { IconVideo, StarIcon } from '@/assets/images';
import { Checkbox, CustomButton } from '@/components';
import ChatProgressLoader from '@/features/chats/ui/components/Progress/ChatProgressLoader';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { useMessageFormatter } from '@/features/chats/viewmodels/useMessageFormatter';
import { useMessageTruncation } from '@/features/chats/viewmodels/useMessageTruncation';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import {
   formatBytes,
   getMediaDimensions,
   getThumbBase64URL,
   millisToMinutesAndSeconds,
} from '@/shared/utils/mediaUtils';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';

const SENDER_CLASS = 'sender-chat ct-rowReverse chat-bubble ct-flex ct-aiEnd ct-gap10 ct-padding-lr8-rl16';
const RECEIVER_CLASS = 'receiver-chat chat-bubble ct-flex ct-gap10 ct-padding-lr8-rl16';
const SENDER_INNER = 'ct-flex ct-flexColEnd ct-relative ct-gap10';
const RECEIVER_INNER = 'ct-flex ct-flexCol ct-relative ct-gap10';

const VideoBox = memo(({ message, onMediaClick, activeChatJid, showProfile, senderId }) => {
   const { t } = useLanguage();
   const { msgBody = {} } = message;
   const { media = {}, mentionedUsersIds = [] } = msgBody;
   const { caption = '', thumb_image = '', duration = 0, file_size = 0, webWidth, webHeight } = media;

   const hasCaption = caption && caption.trim() !== '';
   const { isTruncated, isExpanded, displayText, toggleExpand } = useMessageTruncation(caption);
   const formattedContent = useMessageFormatter(displayText, { mentionedUsersIds, isClickable: true });
   const thumbUrl = thumb_image ? getThumbBase64URL(thumb_image) : '';
   const { width: displayWidth, height: displayHeight } = getMediaDimensions(webWidth, webHeight);

   // Format bytes to match '15 mb' design
   const fileSizeFormatted = file_size ? formatBytes(file_size, 0).toLowerCase() : '';

   return (
      <div
         className={`chat-box chat-img-box ct-flex ct-flexCol ${hasCaption ? 'ct-gap10' : 'no-caption'} ${thumbUrl || thumb_image ? 'videoimagebubble' : ''} ${message.msgBody?.replyTo ? 'reply-bubble' : ''} ${displayWidth < displayHeight ? 'portraitvideo' : ''} `}
         style={{ width: `${displayWidth / 16}rem` }}
      >
         {showProfile && <Username userId={senderId} />}
         <ReplyMessageCard replyTo={message.msgBody?.replyTo} activeChatJid={activeChatJid} className="reply" />

         <div className="document-img ct-flex ct-fcol ct-aiStart ct-gap8 ct-relative">
            <button
               className="imageFile"
               onClick={onMediaClick}
               style={{
                  position: 'relative',
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  width: `${displayWidth / 16}rem`,
                  height: `${displayHeight / 16}rem`,
               }}
            >
               <img src={thumbUrl} alt="Video" className="imageFile" />
               <div className="video-info ct-flex ct-jcBetween">
                  <div className="ct-flex ct-aiCenter ct-gap8">
                     <i className="ct-icon ct-md">
                        <IconVideo />
                     </i>
                     <span>{millisToMinutesAndSeconds(duration)}</span>
                  </div>
                  <span>{fileSizeFormatted}</span>
               </div>
            </button>
            <ChatProgressLoader message={message} />
         </div>
         {hasCaption && (
            <p>
               {formattedContent}
               <br />
               {isTruncated && (
                  <button className="view-more-less-btn" onClick={toggleExpand}>
                     {isExpanded ? t('message.VIEW_LESS') : t('message.VIEW_MORE')}
                  </button>
               )}
            </p>
         )}
      </div>
   );
});

VideoBox.propTypes = {
   message: PropTypes.object.isRequired,
   onMediaClick: PropTypes.func,
   activeChatJid: PropTypes.string,
   showProfile: PropTypes.bool,
   senderId: PropTypes.string,
};

VideoBox.displayName = 'VideoBox';

const VideoCard = memo(({ message, activeChatJid }) => {
   const { t } = useLanguage();
   const { hideAvatar = false, hideActions = false, disableMediaClick = false } = useMessageActionContext();
   const { isSelected, isSelectionActive, handleToggleSelection, handleMediaClick } = useMessageAction(
      message,
      activeChatJid,
   );

   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();

   const timestamp = useMemo(
      () => (message.timestamp ? formatChatTime(new Date(Number(message.timestamp)), 'message-card') : ''),
      [message.timestamp],
   );
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const hideAvatarVideoCard = isSender || hideAvatar;
   const showProfile = isChatGroup && !hideAvatarVideoCard;

   const isHighlighted = message?.isHighlighted;

   return (
      <CustomButton
         as="div"
         className={`message-row custom-videocard ${isSelectionActive && !hideActions ? 'cts-video ct-flex ct-aiCenter selection-active' : ''} ${isSelected && !hideActions ? 'selected' : ''} ${isHighlighted && !hideActions ? 'search-highlight-active' : ''}`}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-video">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}
         <div className={`${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar cts-video">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}
            <div
               className={`${isSender ? SENDER_INNER : RECEIVER_INNER} ${message.favouriteStatus === 1 ? 'starredMessage ' : ''}`}
            >
               <VideoBox
                  message={message}
                  onMediaClick={disableMediaClick ? undefined : handleMediaClick}
                  activeChatJid={activeChatJid}
                  showProfile={showProfile}
                  senderId={senderId}
               />
               <div className={`cts-video ct-flexAlign ct-flex ct-gap8 ${isSender ? 'ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={message.msgStatus} />}
                  <span className="time cts-video">{timestamp}</span>
                  {message.editedStatus === 1 && <span className="edited-icon">{t('message.EDITED')}</span>}
                  {message.favouriteStatus === 1 && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </div>
               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-video"
                     message={message}
                     isSender={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

VideoCard.displayName = 'VideoCard';
VideoCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default VideoCard;
